import { message } from 'antd';
import { AGENT_SCENARIOS_CRUD_ACTION_CREATORS } from '../actions/agent_scenarios_actions';
import FetchUtils from './fetch_utils';
import { AOP_JSON_HEADERS, agentRequest, throughLatest } from './agent_service_request';
import { getAgentIntelligenceUri } from '../utils/EnvConfigUtils';
import { nullSafeGetOrElse } from '../utils/DataAccessUtils';
import {
	AOPActiveWindow,
	AOPScenario,
	AOPScenarioConflict,
	AOPScenarioEntry,
	AOPScope,
} from '../types/AOPTypes';

const fetchUtils = new FetchUtils();

/**
 * Scenarios — the routing rules that pick a procedure — against
 * openwrench-intelligence-service (`/api/v1/buyer/agent/scenarios`).
 *
 * `GET /scenarios` is the only enumeration the service offers: there is no
 * endpoint that lists procedures, so this is what backs the AOP list page. Each
 * entry carries the scenario and the procedure it routes to, if one is linked.
 */

/** What a caller gets if it asks for no particular page. */
const DEFAULT_PAGE_SIZE = 10;

/** `limit` is capped here upstream; asking for more is a 422, not a big page. */
export const AOP_MAX_PAGE_SIZE = 100;

/** Only these are accepted by `sort_by`; anything else is refused. */
export const AOP_SCENARIO_SORT_FIELDS = ['created_at', 'updated_at', 'name'];

/** Query string from the params that are actually set. */
const queryStringOf = (params: { [key: string]: any }): string => {
	const pairs = Object.keys(params)
		.filter((key) => params[key] !== undefined && params[key] !== null && params[key] !== '')
		.map((key) => `${encodeURIComponent(key)}=${encodeURIComponent(String(params[key]))}`);
	return pairs.length > 0 ? `?${pairs.join('&')}` : '';
};

const scenariosUrl = (suffix: string): string =>
	`${getAgentIntelligenceUri()}/api/v1/buyer/agent/scenarios${suffix}`;

/**
 * Two live scenarios of one kind may not share a problem type, a priority and an
 * active window. Business hours and After hours never meet; two custom windows
 * meet only where their hours actually overlap, and any other pair meets.
 *
 * The message used to say a custom window overlaps every other window. That is
 * true of a custom window against a named one and wrong of two custom windows,
 * and it sent authors away from a range that would have been accepted.
 */
export const describeScenarioError = (code: string): string => {
	if (code === 'scenario_scope_conflict') {
		return (
			'Another procedure already covers one of these problem types at one of these priorities. ' +
			'Business hours and After hours never overlap each other; a custom window overlaps either of them, ' +
			'and overlaps another custom window only where their hours meet.'
		);
	}
	if (code === 'procedure_has_scenario') {
		return 'That procedure already has a scenario routing to it.';
	}
	if (code === 'scenario_has_procedure') {
		return 'This scenario is already linked to a procedure.';
	}
	return 'Could not save the scenario.';
};

/** The list record: a scenario entry keyed by its scenario id. */
export interface AOPScenarioRecord extends AOPScenarioEntry {
	id: string;
}

const toRecord = (entry: AOPScenarioEntry): AOPScenarioRecord => ({
	id: entry.scenario.id,
	...entry,
});

/**
 * Records are `{ scenario, procedure }` entries, because that is what the list
 * returns and what every screen reads. `POST` and `PATCH` answer with the
 * scenario alone, so the procedure has to come from somewhere else — here, from
 * whatever the slice already holds for that id.
 */
const toRecordFromScenario = (scenario: AOPScenario, state: any): AOPScenarioRecord => {
	// Bracket access against the whole map, not a `nullSafeGet` path: the keys
	// are scenario ids, not path segments.
	const records = nullSafeGetOrElse('agent_scenarios.records', state, {});
	const existing = records[scenario.id];

	return {
		id: scenario.id,
		scenario,
		procedure: existing ? existing.procedure : null,
	};
};

/**
 * Reads the list in the shape `PaginatedReduxTable` expects.
 *
 * `GET /scenarios` takes `offset` and `limit`, so the page is cut by the
 * service rather than here.
 *
 * It also takes `procedure_id`, which is how a screen wanting one procedure's
 * routing asks for it rather than reading the list and looking. Pass it in
 * `params`; the four screens that need one scenario all do.
 *
 * `search`, `sort_by` and `order` go to the service too, so the list page's
 * search box and its sortable columns narrow the whole collection rather than
 * the page in hand. `sort_by` accepts `created_at`, `updated_at` and `name`;
 * anything else is a 422, so only those columns are offered.
 *
 * The response is `{ data, count }`. The count is the real total now — this
 * used to infer one from how full a page came back, which kept Next reachable
 * but could not say where the end was.
 */
export const readAgentScenarios =
	(
		params: any,
		targetCollectionName: string,
		pagination: any,
		sorting: any,
		filters: any,
		addToTargetCollection: boolean,
		/** See `takeLatestFor`. True for the read a table draws from. */
		takeLatest?: boolean
	) =>
	(dispatch): Promise<AOPScenarioRecord[]> => {
		dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.fetchStart(targetCollectionName));

		// No pagination means the first page, not every scenario — a caller that
		// needs them all says so, because the service decides the page now.
		const current = pagination && pagination.current ? pagination.current : 1;
		const asked = pagination && pagination.pageSize ? pagination.pageSize : DEFAULT_PAGE_SIZE;
		// `limit` is capped at 100 upstream and a larger one is refused, so a page
		// size past it is cut here rather than turned into a 422.
		const pageSize = Math.min(asked, AOP_MAX_PAGE_SIZE);

		/*
		  Filters are merged into the params, as the generated thunks do it: the
		  list page's search box is a filter on the collection, so its text arrives
		  in `filters.search` rather than in `params`.
		*/
		const applied = { ...params, ...filters };

		// `sort_by` takes only these three; anything else is a 422, so a column
		// outside them is not offered and not forwarded.
		const sortBy =
			sorting && AOP_SCENARIO_SORT_FIELDS.indexOf(sorting.sort_by) >= 0 ? sorting.sort_by : null;

		const query = queryStringOf({
			offset: (current - 1) * pageSize,
			limit: pageSize,
			procedure_id: applied.procedure_id,
			// What the list page's search box types. The service matches it, so this
			// narrows the whole collection rather than the page in hand.
			search: applied.search,
			sort_by: sortBy,
			// antd says ascend/descend and the service says asc/desc.
			order: sortBy && sorting.order ? (sorting.order === 'ascend' ? 'asc' : 'desc') : null,
		});

		const request = fetchUtils.get(scenariosUrl(query), { headers: AOP_JSON_HEADERS });

		return agentRequest<AOPScenarioRecord[]>(
			dispatch,
			/*
			  Only the newest answer, for the read a table draws from. The stream is
			  named after the collection and prefixed, so it cannot meet the
			  execution feeds in the shared registry.

			  Asked for rather than assumed, because this one thunk is both the
			  generated `read` and the generated `readOne`: the list page draws a
			  collection, and four screens use it to look up a single
			  `procedure_id`. Those lookups are independent facts that pile up in
			  `records` — the activity page asks for several at once — so cancelling
			  them against each other would lose every name but the last.
			*/
			throughLatest(takeLatest ? `scenarios:${targetCollectionName}` : undefined, request),
			{
				fallback: 'scenarios_unavailable',
				onFailure: (code) =>
					dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.fetchError(code, targetCollectionName)),
				onSuccess: (json) => {
					/*
					  `{ data, count }`, not a bare array. Read loudly rather than
					  defensively: the rows were the body itself until the service
					  wrapped them, and a shape we do not expect would otherwise empty
					  the list with nothing said. Thrown, so it is reported as the code.
					*/
					const rows = json ? json.data : null;

					if (!Array.isArray(rows)) {
						throw new Error('scenarios_unexpected_shape');
					}

					const page = (rows as AOPScenarioEntry[]).map(toRecord);
					const total = json.count;

					dispatch(
						AGENT_SCENARIOS_CRUD_ACTION_CREATORS.fetchSuccess(
							page,
							total,
							targetCollectionName,
							/*
							  The total goes in the pagination too, not only beside it. The
							  reducer stores `action.pagination` verbatim and
							  `PaginatedReduxTable` reads `pagination.total` from there; the
							  `count` it also stores is read by nobody.

							  The page size is the clamped one, not the one asked for: a
							  saved URL can ask past the service's cap of 100, and handing
							  antd the larger number while a hundred rows came back makes
							  its page boundaries disagree with the data.
							*/
							{ ...pagination, pageSize, total },
							sorting,
							filters,
							addToTargetCollection
						)
					);

					return page;
				},
			}
		);
	};

export interface AOPScenarioInput {
	procedure_id: string | null;
	problem_type_ids: AOPScope;
	priority_ids: AOPScope;
	active_window: AOPActiveWindow;
}

/**
 * Which live scenarios a scope would collide with, asked before anything is
 * written. `scenario_id` names the scenario being edited so it does not count
 * as colliding with itself.
 *
 * An empty list means the scope is free. This is what lets the create flow
 * refuse a clashing scope before a procedure exists, rather than creating one
 * and stranding it when the scenario is rejected.
 */
export const checkScenarioConflicts =
	(input: Omit<AOPScenarioInput, 'procedure_id'> & { scenario_id?: string | null }) =>
	(dispatch): Promise<AOPScenarioConflict[]> =>
		agentRequest<AOPScenarioConflict[]>(
			dispatch,
			fetchUtils.post(scenariosUrl('/conflicts'), {
				headers: AOP_JSON_HEADERS,
				// `dispatch` is the only kind; the service requires it named.
				data: { kind: 'dispatch', ...input },
			}),
			{
				fallback: 'conflict_check_failed',
				// Nothing in the slice is about to change, so there is nothing to
				// stand down — the caller reports this one.
				onFailure: () => undefined,
				onSuccess: (json) => json,
			}
		);

export const createAgentScenario =
	(input: AOPScenarioInput) =>
	(dispatch, getState): Promise<AOPScenario> => {
		dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.createStart(input));

		return agentRequest<AOPScenario>(
			dispatch,
			fetchUtils.post(scenariosUrl(''), { headers: AOP_JSON_HEADERS, data: input }),
			{
				fallback: 'scenario_not_created',
				onFailure: (code) =>
					dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.createError(code, input)),
				onSuccess: (json) => {
					dispatch(
						AGENT_SCENARIOS_CRUD_ACTION_CREATORS.createSuccess(
							toRecordFromScenario(json, getState())
						)
					);
					return json;
				},
			}
		);
	};

/**
 * Changes a scenario's scope or active window. Every field is optional upstream,
 * so this sends only what the form produced — a `PATCH` with the whole record
 * would rewrite fields nobody touched.
 *
 * The overlap rules are the service's: two scenarios whose scope and window
 * intersect are rejected with `scenario_scope_conflict`, and that is reported
 * rather than pre-empted here.
 */
export const updateAgentScenario =
	(scenarioId: string, input: Partial<AOPScenarioInput>) =>
	(dispatch, getState): Promise<AOPScenario> => {
		dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.updateStart({ id: scenarioId }));

		return agentRequest<AOPScenario>(
			dispatch,
			fetchUtils.patch(scenariosUrl(`/${scenarioId}`), {
				headers: AOP_JSON_HEADERS,
				data: input,
			}),
			{
				fallback: 'scenario_not_updated',
				onFailure: (code) =>
					dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.updateError(code, { id: scenarioId })),
				onSuccess: (json) => {
					dispatch(
						AGENT_SCENARIOS_CRUD_ACTION_CREATORS.updateSuccess(
							toRecordFromScenario(json, getState())
						)
					);
					return json;
				},
			}
		);
	};

/**
 * Deletes the scenario **and its procedure** — the service soft-deletes both.
 *
 * Verified in `delete_scenario` in `domain/authoring.py` (openwrench-intelligence-service, `mani/radar-authoring-and-executions`):
 * it stamps `deleted_at` on the scenario and, when `procedure_id` is set, on
 * the procedure too, under the tenant lock. The pair really does go together,
 * which is what the delete confirmations promise.
 */
export const deleteAgentScenario =
	(scenarioId: string) =>
	(dispatch): Promise<void> => {
		dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.deleteStart({ id: scenarioId }));

		// 204, so there is no body — `parseJSON` hands back the response itself,
		// which still says whether it worked.
		return agentRequest<void>(
			dispatch,
			fetchUtils.delete(scenariosUrl(`/${scenarioId}`), { headers: AOP_JSON_HEADERS }),
			{
				fallback: 'scenario_not_deleted',
				onFailure: (code) => {
					dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.deleteError(code, { id: scenarioId }));
					message.error('Could not delete this procedure.');
				},
				onSuccess: () => {
					dispatch(AGENT_SCENARIOS_CRUD_ACTION_CREATORS.deleteSuccess({ id: scenarioId }));
					return undefined;
				},
			}
		);
	};
